import * as Dialog from '@radix-ui/react-dialog';
import { Title, MutedText, CancelBtn } from '../../../account/account-settings.styled';
import { OnboardBtn } from '../../../profile/profile.styled';
import { Divider, DescriptionList, BulletItem } from '../../billing.styled';
import {
  Overlay,
  ContentShell,
  DialogPanel,
  PlanCard,
  PlanHeader,
  Badge,
  FeatureListWrap,
} from './upgrade-sanctum.styled';

import {
  useUpgradeSanctum,
  getBillingCycleCopy,
  UpgradeSanctumProps,
} from './upgrade-sanctum.hooks';
import { VSpace } from '../../../account/account-settings';
import { RadioButton } from '../../../../../../components/radio-button/radio-button';
import { PLANS, formatDollars } from '../../billing.constants';

function renderFeatureList(items: string[]) {
  return (
    <FeatureListWrap>
      <DescriptionList>
        {items.map((text, idx) => (
          <BulletItem key={idx}>{text}</BulletItem>
        ))}
      </DescriptionList>
    </FeatureListWrap>
  );
}

export default function UpgradeNowSanctumModal({
  open,
  onClose,
  onConfirm,
  // trialEndsOn, // currently unused but kept for API compatibility
  // currentPlan, // currently unused but kept for API compatibility
}: UpgradeSanctumProps) {
  const { selectedPlan, setSelectedPlan, billingCycle, setBillingCycle, confirm, allFeatures } =
    useUpgradeSanctum({ onConfirm });

  const billingCopy = getBillingCycleCopy(billingCycle);
  const ns = PLANS.northStar.pricing;

  return (
    <Dialog.Root open={open} onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <Dialog.Portal>
        <Overlay />
        <ContentShell aria-modal="true" role="dialog" aria-labelledby="upgrade-plan-title" aria-describedby={undefined}>
          <Dialog.Title style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
            Upgrade plan
          </Dialog.Title>
          <DialogPanel as="section">
            <VSpace $h={4} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Title id="upgrade-plan-title">Upgrade to North Star</Title>

              <VSpace $h={16} />

              {/* Plans radiogroup */}
              <div role="radiogroup" aria-labelledby="choose-plan-label">
                <PlanCard
                  type="button"
                  onClick={() => setSelectedPlan('North Star')}
                  $active={selectedPlan === 'North Star'}
                  role="radio"
                  aria-checked={selectedPlan === 'North Star'}
                  aria-label="North Star plan"
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <PlanHeader>
                      <RadioButton
                        name="plan"
                        value="North Star"
                        checked={selectedPlan === 'North Star'}
                        onChange={() => setSelectedPlan('North Star')}
                      />
                      <Title style={{ fontSize: '15px' }}>North Star</Title>
                    </PlanHeader>
                    <Badge
                      style={{
                        border: '1px solid #5E84ED',
                        borderRadius: '12px',
                        color: '#5E84ED',
                        width: '92px',
                        height: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      Best value
                    </Badge>
                  </div>

                  <VSpace $h={12} />
                  <MutedText style={{ fontWeight: 600, fontSize: '14px' }}>
                    Annual pricing
                  </MutedText>
                  <VSpace $h={4} />
                  <Title>{formatDollars(ns.annualMonthly)}/mo</Title>
                  <VSpace $h={4} />
                  <MutedText style={{ fontSize: '14px', lineHeight: 1.4, fontWeight: 400 }}>
                    {formatDollars(ns.annualTotal)} billed at outset of 12-month subscription
                  </MutedText>
                  <VSpace $h={4} />
                  <Title style={{ fontSize: '14px', lineHeight: 1.4, color: '#8FB0FF' }}>
                    Saving {formatDollars(ns.annualSavings)} per annum
                  </Title>

                  <VSpace $h={12} />
                  <Divider />

                  {renderFeatureList(allFeatures)}
                </PlanCard>
              </div>

              <VSpace $h={48} />

              {/* Billing cycle radios */}
              <MutedText id="choose-cycle-label" style={{ fontWeight: 500, fontSize: '14px' }}>
                Choose your billing cycle
              </MutedText>
              <VSpace $h={12} />

              <div role="radiogroup" aria-labelledby="choose-cycle-label">
                <PlanCard
                  type="button"
                  onClick={() => setBillingCycle('Annual')}
                  $active={billingCycle === 'Annual'}
                  role="radio"
                  aria-checked={billingCycle === 'Annual'}
                  aria-label="Annual subscription"
                >
                  <PlanHeader>
                    <RadioButton
                      name="billingCycle"
                      value="Annual"
                      checked={billingCycle === 'Annual'}
                      onChange={() => setBillingCycle('Annual')}
                    />
                    <Title style={{ fontSize: '15px' }}>Annual Subscription</Title>
                  </PlanHeader>
                  <div style={{ paddingLeft: '29px' }}>
                    <VSpace $h={4} />
                    <MutedText>{formatDollars(ns.annualMonthly)} per month (after discount)</MutedText>
                    <VSpace $h={4} />
                    <MutedText style={{ lineHeight: '1.4' }}>
                      {formatDollars(ns.annualTotal)} billed at outset of 12-month subscription{' '}
                    </MutedText>
                    <VSpace $h={4} />
                    <MutedText
                      style={{
                        fontWeight: '600',
                        color: '#8FB0FF',
                      }}
                    >
                      Save {formatDollars(ns.annualSavings)} per annum
                    </MutedText>
                  </div>
                </PlanCard>

                <VSpace $h={12} />

                <PlanCard
                  type="button"
                  onClick={() => setBillingCycle('Monthly')}
                  $active={billingCycle === 'Monthly'}
                  role="radio"
                  aria-checked={billingCycle === 'Monthly'}
                  aria-label="Monthly subscription"
                >
                  <PlanHeader>
                    <RadioButton
                      name="billingCycle"
                      value="Monthly"
                      checked={billingCycle === 'Monthly'}
                      onChange={() => setBillingCycle('Monthly')}
                    />
                    <Title style={{ fontSize: '15px' }}>Monthly Subscription</Title>
                  </PlanHeader>
                  <div style={{ paddingLeft: '29px' }}>
                    <VSpace $h={4} />
                    <MutedText>{formatDollars(ns.monthly)} billed each month</MutedText>
                    <VSpace $h={4} />
                    <MutedText style={{ lineHeight: '1.4' }}>
                      Pay as you go - cancel anytime{' '}
                    </MutedText>
                  </div>
                </PlanCard>
              </div>

              <VSpace $h={16} />
              <MutedText>{billingCopy}</MutedText>
              <VSpace $h={16} />

              <OnboardBtn
                type="button"
                onClick={confirm}
                aria-label={`Confirm ${selectedPlan} plan with ${billingCycle} billing`}
              >
                Confirm Plan
              </OnboardBtn>

              <VSpace $h={16} />

              <Dialog.Close asChild>
                <CancelBtn type="button" aria-label="Close upgrade modal">
                  Close
                </CancelBtn>
              </Dialog.Close>
            </div>
            <VSpace $h={51} />
          </DialogPanel>
        </ContentShell>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
